/**
 * A minimal, dependency-free .xlsx reader: File/ArrayBuffer → { [sheetName]: rows[][] }.
 *
 * Built for the สปสช. "รายงานสรุปการขอเบิกชดเชย" export (NhsoImport.jsx), which is the
 * only spreadsheet the CRM reads. It returns cell VALUES only — no styles, no formulas,
 * no merged-cell expansion — because the parser above it locates everything by header
 * text, not by layout.
 *
 * WHY NOT A LIBRARY. The npm `xlsx` package is frozen at 0.18.5 with published
 * advisories (prototype pollution, ReDoS) and the fixed builds live off-npm; exceljs is
 * ~1 MB for a job this size. An .xlsx is a zip of XML files, the browser already ships
 * a deflate decoder (`DecompressionStream('deflate-raw')`), and Node 18+ has the same
 * global, so the reader runs unchanged in the tests.
 *
 * WHAT IT HANDLES: stored + deflated entries; cell types inlineStr (what the NHSO
 * portal writes — its sharedStrings.xml is empty), s (shared strings, what Excel writes
 * when someone re-saves the file), str, b, e and numbers; rich-text runs; numeric and
 * named XML entities. WHAT IT REFUSES: ZIP64 and encrypted workbooks, loudly.
 */

const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

async function inflateRaw(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** zip bytes → Map(path → Uint8Array). Only the central directory is trusted for sizes. */
export async function unzip(buf) {
  const b = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let eocd = -1
  // The end-of-central-directory record sits in the last 22 + 65535 (comment) bytes.
  for (let i = b.length - 22; i >= Math.max(0, b.length - 22 - 65535); i--) {
    if (u32(b, i) === 0x06054b50) { eocd = i; break }
  }
  if (eocd < 0) throw new Error('ไม่ใช่ไฟล์ .xlsx (อ่านโครงสร้าง zip ไม่ได้)')

  const count = u16(b, eocd + 10)
  let p = u32(b, eocd + 16)
  if (p === 0xffffffff) throw new Error('ไฟล์ใหญ่เกินไป (ZIP64) — ไม่รองรับ')

  const out = new Map()
  const dec = new TextDecoder()
  for (let i = 0; i < count; i++) {
    if (u32(b, p) !== 0x02014b50) throw new Error('โครงสร้าง zip เสีย')
    const flags = u16(b, p + 8)
    const method = u16(b, p + 10)
    const csize = u32(b, p + 20)
    const nlen = u16(b, p + 28)
    const xlen = u16(b, p + 30)
    const clen = u16(b, p + 32)
    const local = u32(b, p + 42)
    const name = dec.decode(b.subarray(p + 46, p + 46 + nlen))
    p += 46 + nlen + xlen + clen

    if (flags & 1) throw new Error('ไฟล์ถูกตั้งรหัสผ่าน — เปิดใน Excel แล้วบันทึกแบบไม่มีรหัสก่อน')
    if (u32(b, local) !== 0x04034b50) throw new Error('โครงสร้าง zip เสีย')
    const start = local + 30 + u16(b, local + 26) + u16(b, local + 28)
    const data = b.subarray(start, start + csize)
    if (method === 0) out.set(name, data)
    else if (method === 8) out.set(name, await inflateRaw(data))
    // Any other method is something no spreadsheet writer uses; skipping it means the
    // sheet that needed it comes back missing, which the caller reports by name.
  }
  return out
}

/* ── XML, by regex ──────────────────────────────────────────────────────────
 * Spreadsheet XML is machine-written and flat, so a handful of anchored regexes are
 * enough, and they behave identically in the browser and in Node (no DOMParser there). */

const ENT = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }
export function decodeXml(s) {
  return String(s).replace(/&(#x[0-9a-fA-F]+|#\d+|[a-z]+);/g, (m, e) => {
    if (e[0] === '#') {
      const cp = e[1] === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m
    }
    return ENT[e] ?? m
  })
}

const attrs = (s) => {
  const o = {}
  for (const m of String(s).matchAll(/([\w:]+)="([^"]*)"/g)) o[m[1]] = decodeXml(m[2])
  return o
}

/** All <t> runs inside a fragment, joined — covers plain and rich-text strings. */
const textRuns = (frag) => [...String(frag).matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>|<t\b[^>]*\/>/g)]
  .map((m) => decodeXml(m[1] ?? '')).join('')

/** "AB12" → 27 (0-based column). */
export function colIndex(ref) {
  const letters = /^([A-Z]+)/.exec(String(ref))?.[1] ?? ''
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

/** One worksheet's XML → dense rows[][] (index 0 = spreadsheet row 1). */
export function parseSheetXml(xml, shared = []) {
  const rows = []
  for (const rm of String(xml).matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const ra = attrs(rm[1])
    const r = Number(ra.r) > 0 ? Number(ra.r) - 1 : rows.length
    const cells = []
    for (const cm of String(rm[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const ca = attrs(cm[1])
      const inner = cm[2] ?? ''
      const c = ca.r ? colIndex(ca.r) : cells.length
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1]
      let val = null
      switch (ca.t) {
        case 'inlineStr': val = textRuns(inner); break
        case 's': val = shared[Number(v)] ?? ''; break
        case 'b': val = v === '1'; break
        case 'str': case 'e': val = v == null ? '' : decodeXml(v); break
        default: val = v == null || v === '' ? null : Number(v)
      }
      cells[c] = val
    }
    for (let i = 0; i < cells.length; i++) if (cells[i] === undefined) cells[i] = null
    rows[r] = cells
  }
  for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = []
  return rows
}

/** zip entries → { sheetName: rows[][] }, in workbook order. */
export function sheetsFromEntries(entries) {
  const dec = new TextDecoder()
  const text = (path) => (entries.has(path) ? dec.decode(entries.get(path)) : '')

  const shared = [...text('xl/sharedStrings.xml').matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)].map((m) => textRuns(m[1]))

  const rels = new Map()
  for (const m of text('xl/_rels/workbook.xml.rels').matchAll(/<Relationship\b([^>]*?)\/?>/g)) {
    const a = attrs(m[1])
    const target = String(a.Target ?? '').replace(/^\/?(xl\/)?/, '')
    rels.set(a.Id, `xl/${target}`)
  }

  const out = {}
  for (const m of text('xl/workbook.xml').matchAll(/<sheet\b([^>]*?)\/?>/g)) {
    const a = attrs(m[1])
    const path = rels.get(a['r:id'])
    if (!a.name || !path || !entries.has(path)) continue
    out[a.name] = parseSheetXml(text(path), shared)
  }
  return out
}

/** File | Blob | ArrayBuffer → { sheetName: rows[][] } */
export async function readXlsx(input) {
  const buf = input instanceof ArrayBuffer || input instanceof Uint8Array ? input : await input.arrayBuffer()
  const entries = await unzip(buf)
  if (!entries.has('xl/workbook.xml')) throw new Error('ไม่ใช่ไฟล์ Excel (.xlsx)')
  return sheetsFromEntries(entries)
}
